import { getBoundingSquare } from "@/features/sku-sections/lib/get-bounding-square";
import { POLYGON_COLORS } from "@/features/sku-sections/lib/polygon-colors";
import { Flex, Spinner } from "@radix-ui/themes";
import React, { memo, useMemo } from "react";
import { Image as KonvaImage, Layer, Line, Stage } from "react-konva";
import { useMeasure } from "react-use";
import useImage from "use-image";

interface PolygonCropProps {
  imageUrl: string;
  originalWidth: number;
  originalHeight: number;
  polygonPoints: [number, number][];
}

const PolygonCrop: React.FC<PolygonCropProps> = memo(
  ({ imageUrl, ...props }) => {
    const [image, status] = useImage(imageUrl);
    const [ref, { width: currentSize }] = useMeasure<HTMLDivElement>();

    return (
      <div ref={ref} className="rounded overflow-hidden w-full h-full">
        {status === "loading" ? (
          <Flex
            className="w-full h-full bg-gray-200"
            justify="center"
            align="center"
          >
            <Spinner />
          </Flex>
        ) : status === "loaded" ? (
          <PolygonCropCanvas
            currentSize={currentSize}
            image={image}
            {...props}
          />
        ) : null}
      </div>
    );
  }
);

function PolygonCropCanvas({
  image,
  originalWidth,
  originalHeight,
  polygonPoints,
  currentSize,
}: Omit<PolygonCropProps, "imageUrl"> & {
  image: HTMLImageElement | undefined;
  currentSize: number;
}) {
  const boundingSquare = useMemo(() => {
    return getBoundingSquare(polygonPoints, originalWidth, originalHeight, 0.1);
  }, [polygonPoints, originalWidth, originalHeight]);

  const flatPoints = useMemo(() => {
    const adjust = (vertex: number) => {
      return (vertex / boundingSquare.size) * currentSize;
    };

    return polygonPoints
      .map(([x, y]) => {
        const sourceX = x * originalWidth - boundingSquare.x;
        const sourceY = y * originalHeight - boundingSquare.y;

        const adjustedWidth = adjust(sourceX);
        const adjustedHeight = adjust(sourceY);

        return [adjustedWidth, adjustedHeight];
      })
      .flatMap((point) => point);
  }, [polygonPoints, boundingSquare, originalWidth, originalHeight]);

  const scaleRatio = boundingSquare.size / currentSize;

  if (currentSize <= 0) {
    return null;
  }

  return (
    <Stage width={currentSize} height={currentSize}>
      <Layer>
        <KonvaImage
          clipWidth={boundingSquare.size}
          clipHeight={boundingSquare.size}
          x={-boundingSquare.x / scaleRatio}
          y={-boundingSquare.y / scaleRatio}
          width={originalWidth / scaleRatio}
          height={originalHeight / scaleRatio}
          image={image}
        />
        <Line
          points={flatPoints}
          stroke={POLYGON_COLORS.SELECTED.STROKE}
          strokeWidth={1.5}
          closed
          hitStrokeWidth={0}
          shadowForStrokeEnabled={false}
        />
      </Layer>
    </Stage>
  );
}

export default PolygonCrop;
